/** Versioned jersey identifiers. Only the server allocates permanent codes. */
export const JERSEY_SKU_KINDS = [
  'collection',
  'kit',
  'sleeve',
  'print',
  'edition',
  'fit',
  'collar',
  'audience',
  'colour',
  'size',
] as const;
export type JerseySkuKind = (typeof JERSEY_SKU_KINDS)[number];
export const JERSEY_DETAIL_KINDS = JERSEY_SKU_KINDS.filter(
  (kind) => kind !== 'colour' && kind !== 'size',
);
export interface JerseyToken {
  key: string;
  label: string;
  token: string;
  aliases: string[];
  active?: boolean;
  print_kind?: 'no_name' | 'named_player' | 'customisable' | 'unspecified';
  player_name?: string;
  shirt_number?: string | null;
}
export interface JerseyIdentifierConfig {
  format_version: 1;
  enabled: boolean;
  teams: { team_id: string; code: string; aliases: string[] }[];
  tokens: Record<JerseySkuKind, JerseyToken[]>;
}
export interface JerseySkuDetails {
  team_id: string;
  period: { kind: 'season' | 'calendar_year' | 'unspecified'; value: string | null };
  selections: Partial<Record<JerseySkuKind, string>>;
}
export interface JerseySkuReview {
  configuration_version_id: string;
  confirmed: boolean;
  details: JerseySkuDetails;
  legacy_variants?: { variant_id: string; colour_key: string; size_key: string }[];
}
export interface JerseyIdentifierContext {
  existing_product?: boolean;
  enabled: boolean;
  configuration_version_id: string | null;
  config: JerseyIdentifierConfig | null;
  teams: { id: string; name: string }[];
  review: JerseySkuReview | null;
  legacy_variants?: {
    id: string;
    sku: string;
    color: string | null;
    size: string;
    colour_key: string | null;
    size_key: string | null;
  }[];
}
export const jerseyMatchKey = (value: string) => value.trim().replace(/\s+/g, ' ').toLowerCase();
export function cleanJerseyConfig(config: JerseyIdentifierConfig): JerseyIdentifierConfig {
  const aliases = (values: string[]) => values.map((v) => v.trim()).filter(Boolean);
  return {
    ...config,
    teams: config.teams.map((t) => ({ ...t, aliases: aliases(t.aliases) })),
    tokens: Object.fromEntries(
      JERSEY_SKU_KINDS.map((kind) => [
        kind,
        config.tokens[kind].map((entry) => ({ ...entry, aliases: aliases(entry.aliases) })),
      ]),
    ) as JerseyIdentifierConfig['tokens'],
  };
}
export function emptyJerseyConfig(): JerseyIdentifierConfig {
  return {
    format_version: 1,
    enabled: false,
    teams: [],
    tokens: {
      collection: [],
      kit: [],
      sleeve: [],
      print: [],
      edition: [],
      fit: [],
      collar: [],
      audience: [],
      colour: [],
      size: [],
    },
  };
}
export function emptyJerseyDetails(): JerseySkuDetails {
  return { team_id: '', period: { kind: 'unspecified', value: null }, selections: {} };
}
/** User-requested examples, loaded into a draft only; never assigned to a product automatically. */
export function jerseyStarterConfig(config: JerseyIdentifierConfig): JerseyIdentifierConfig {
  const entries = (values: [string, string, string][]): JerseyToken[] =>
    values.map(([key, label, token]) => ({ key, label, token, aliases: [], active: true }));
  const starter = emptyJerseyConfig();
  starter.tokens.collection = entries([
    ['REGULAR', 'Regular', 'REG'],
    ['RETRO', 'Retro', 'RET'],
    ['SPECIAL', 'Special', 'SPE'],
    ['UNSPECIFIED', 'Unspecified', 'UNK'],
  ]);
  starter.tokens.kit = entries([
    ['HOME', 'Home', 'H'],
    ['AWAY', 'Away', 'A'],
    ['THIRD', 'Third', 'T'],
    ['FOURTH', 'Fourth', 'F'],
    ['GOALKEEPER', 'Goalkeeper', 'GK'],
    ['TRAINING', 'Training', 'TR'],
    ['SPECIAL', 'Special', 'SP'],
    ['UNSPECIFIED', 'Unspecified', 'UN'],
  ]);
  starter.tokens.sleeve = entries([
    ['HALF', 'Half Sleeve', 'HS'],
    ['FULL', 'Full Sleeve', 'FS'],
    ['SLEEVELESS', 'Sleeveless', 'SL'],
    ['UNSPECIFIED', 'Unspecified', 'UNK'],
  ]);
  starter.tokens.print = [
    { ...entries([['NO_NAME', 'No Name', 'NN']])[0]!, print_kind: 'no_name' },
    { ...entries([['CUSTOMISABLE', 'Customisable', 'CUST']])[0]!, print_kind: 'customisable' },
    { ...entries([['UNSPECIFIED', 'Unspecified', 'UNK']])[0]!, print_kind: 'unspecified' },
  ];
  starter.tokens.edition = entries([
    ['STANDARD', 'Standard', 'STD'],
    ['DRAGON', 'Dragon', 'DRAGON'],
    ['GOAT', 'GOAT', 'GOAT'],
    ['UNSPECIFIED', 'Unspecified', 'UNK'],
  ]);
  starter.tokens.fit = entries([
    ['FAN', 'Fan', 'FAN'],
    ['PLAYER', 'Player', 'PLAYER'],
    ['UNSPECIFIED', 'Unspecified', 'UN'],
  ]);
  starter.tokens.collar = entries([
    ['ROUND_NECK', 'Round Neck', 'RN'],
    ['V_NECK', 'V Neck', 'VN'],
    ['POLO', 'Polo', 'POLO'],
    ['UNSPECIFIED', 'Unspecified', 'UN'],
  ]);
  starter.tokens.audience = entries([
    ['ADULT_UNISEX', 'Adult Unisex', 'AU'],
    ['UNSPECIFIED', 'Unspecified', 'UNK'],
  ]);
  starter.tokens.colour = entries([
    ['BLACK', 'Black', 'BLK'],
    ['WHITE', 'White', 'WHT'],
    ['BLACK_VIOLET', 'Black/Violet', 'BKV'],
    ['UNSPECIFIED', 'Unspecified', 'UNK'],
  ]);
  starter.tokens.size = entries([
    ['S', 'S', 'S'],
    ['M', 'M', 'M'],
    ['L', 'L', 'L'],
    ['XL', 'XL', 'XL'],
    ['XXL', 'XXL', 'XXL'],
    ['UNSPECIFIED', 'Unspecified', 'UNK'],
  ]);
  starter.tokens.size.find((entry) => entry.key === 'XXL')!.aliases = ['2XL'];
  return {
    ...config,
    tokens: Object.fromEntries(
      JERSEY_SKU_KINDS.map((kind) => [
        kind,
        config.tokens[kind].length ? config.tokens[kind] : starter.tokens[kind],
      ]),
    ) as JerseyIdentifierConfig['tokens'],
  };
}
export function jerseyPeriodToken(period: JerseySkuDetails['period']): string {
  if (period.kind === 'unspecified') {
    if (period.value != null && period.value !== '')
      throw new Error('Clear the unspecified period value.');
    return 'UNK';
  }
  const value = period.value || '';
  if (period.kind === 'calendar_year' && /^[0-9]{4}$/.test(value) && Number(value) > 0)
    return `Y${value}`;
  const match = value.match(/^([0-9]{4})\/([0-9]{2}|[0-9]{4})$/);
  if (period.kind === 'season' && match) {
    const start = Number(match[1]);
    const end =
      match[2]!.length === 2
        ? Math.floor((start + 1) / 100) * 100 + Number(match[2])
        : Number(match[2]);
    if (start > 0 && start < 9999 && end === start + 1)
      return `S${match[1]!.slice(-2)}${String(end).slice(-2)}`;
  }
  throw new Error('Use a calendar year such as 2015 or a season such as 2026/27.');
}
export function jerseyVariantToken(
  config: JerseyIdentifierConfig,
  kind: 'colour' | 'size',
  value: string,
) {
  const key = jerseyMatchKey(value);
  const matches = config.tokens[kind].filter(
    (entry) =>
      entry.active !== false &&
      [entry.key, entry.label, entry.token, ...entry.aliases].some(
        (alias) => jerseyMatchKey(alias) === key,
      ),
  );
  if (!key || matches.length !== 1)
    throw new Error(`Choose a configured ${kind}; '${value || 'blank'}' has no unique mapping.`);
  return matches[0]!;
}
export function jerseyCodePreview(
  config: JerseyIdentifierConfig,
  details: JerseySkuDetails,
  existingCode?: string,
) {
  if (existingCode) return existingCode;
  const team = config.teams.find((t) => t.team_id === details.team_id);
  if (!team) throw new Error('Choose a configured team for the product code.');
  return `${team.code}-Assigned on save`;
}
export function jerseySkuPreview(
  config: JerseyIdentifierConfig,
  details: JerseySkuDetails,
  code: string,
  colour: string,
  size: string,
) {
  const parts = [code, jerseyPeriodToken(details.period)];
  for (const kind of JERSEY_DETAIL_KINDS) {
    const entry = config.tokens[kind].find(
      (t) => t.key === details.selections[kind] && t.active !== false,
    );
    if (!entry) throw new Error(`Review ${kind} for the SKU.`);
    parts.push(entry.token);
  }
  parts.push(
    jerseyVariantToken(config, 'colour', colour).token,
    jerseyVariantToken(config, 'size', size).token,
  );
  const result = parts.join('-');
  if (result.length > 512)
    throw new Error('The detailed SKU exceeds 512 characters. Review the configured tokens.');
  return result;
}
export function assertJerseyReview(
  context: JerseyIdentifierContext,
  review: JerseySkuReview | null | undefined,
): asserts review is JerseySkuReview {
  if (!review?.confirmed || review.configuration_version_id !== context.configuration_version_id)
    throw new Error('Review and confirm the current jersey SKU details.');
  const existing = context.legacy_variants || [];
  if (existing.length) {
    const mappings = review.legacy_variants || [];
    if (
      mappings.length !== existing.length ||
      new Set(mappings.map((m) => m.variant_id)).size !== existing.length
    )
      throw new Error('Review every existing variant colour and size before adding a new SKU.');
    for (const variant of existing) {
      const mapping = mappings.find((m) => m.variant_id === variant.id);
      if (
        !mapping ||
        !context.config?.tokens.colour.some((t) => t.key === mapping.colour_key) ||
        !context.config.tokens.size.some((t) => t.key === mapping.size_key)
      )
        throw new Error(`Review the existing colour and size mapping for ${variant.sku}.`);
    }
  }
}
